import {
  IonContent,
  IonPage,
  IonButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { SignedIn, SignedOut, useUser } from '@clerk/clerk-react';
import { logInOutline, personAddOutline, arrowForwardOutline, createOutline } from 'ionicons/icons';
import { usePageView } from '../hooks/useLoggerInit';
import { useI18n } from '../i18n/I18nProvider';
import { APP_VERSION, GIT_COMMIT } from '../version';

const HomePage: React.FC = () => {
  const history = useHistory();
  const { user } = useUser();
  const { t, locale, setLocale } = useI18n();

  // Track page view
  usePageView('home');

  return (
    <IonPage>
      <IonContent fullscreen className="ion-padding vault-screen home-content">
        <div className="home-container">
          <div className="home-hero">
            <div className="hero-icon">👋</div>
            <h1>{t('home.title')}</h1>
            <p>{t('home.subtitle')}</p>
          </div>

          <SignedOut>
            <div className="home-actions">
              <IonButton
                expand="block"
                className="btn-full-width"
                onClick={() => history.push('/quick')}
              >
                <IonIcon slot="start" icon={createOutline} />
                {t('home.quickCompose')}
              </IonButton>

              <IonButton
                expand="block"
                fill="outline"
                className="btn-full-width"
                onClick={() => history.push('/sign-in')}
              >
                <IonIcon slot="start" icon={logInOutline} />
                {t('home.signIn')}
              </IonButton>
              
              <IonButton
                expand="block"
                fill="clear"
                className="btn-full-width"
                onClick={() => history.push('/sign-up')}
              >
                <IonIcon slot="start" icon={personAddOutline} />
                {t('home.signUp')}
              </IonButton>
            </div>
          </SignedOut>

          <SignedIn>
            <div className="home-welcome">
              <p className="welcome-text">
                {t('home.welcomeBack', {
                  name: user?.firstName || user?.emailAddresses[0]?.emailAddress || '',
                })}
              </p>
              <IonButton
                expand="block"
                className="btn-full-width"
                onClick={() => history.push('/dashboard')}
              >
                {t('home.goToDashboard')}
                <IonIcon slot="end" icon={arrowForwardOutline} />
              </IonButton>
            </div>
          </SignedIn>

          {/* <div className="home-features">
            <div className="feature-item">
              <span className="feature-icon">🔐</span>
              <span>Secure Authentication</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📱</span>
              <span>Cross-Platform</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">⚡</span>
              <span>Fast & Reliable</span>
            </div>
          </div> */}

          <div className="home-footer">
            <div className="language-switcher">
              <IonItem lines="none">
                <IonLabel>{t('languageSwitcher.label')}</IonLabel>
                <IonSelect
                  value={locale}
                  interface="popover"
                  onIonChange={(e) => setLocale(e.detail.value)}
                >
                  <IonSelectOption value="en">{t('languageSwitcher.en')}</IonSelectOption>
                  <IonSelectOption value="ro">{t('languageSwitcher.ro')}</IonSelectOption>
                  <IonSelectOption value="de">{t('languageSwitcher.de')}</IonSelectOption>
                  <IonSelectOption value="es">{t('languageSwitcher.es')}</IonSelectOption>
                  <IonSelectOption value="fr">{t('languageSwitcher.fr')}</IonSelectOption>
                </IonSelect>
              </IonItem>
            </div>
            <div className="footer-links">
              <span onClick={() => history.push('/terms')}>{t('home.terms')}</span>
              <span className="divider">•</span>
              <span onClick={() => history.push('/privacy')}>{t('home.privacy')}</span>
            </div>
            <p className="copyright">
              {t('home.copyright', { year: new Date().getFullYear() })}
            </p>
            <p className="build-meta" aria-label={t('home.buildMeta')}>
              {t('home.buildMeta')}: {APP_VERSION} · {GIT_COMMIT}
            </p>
          </div>
        </div>

        <style>{`
          .home-container {
            display: flex;
            flex-direction: column;
            min-height: 100%;
            padding: 48px 24px;
          }

          .home-content {
            --background:
              radial-gradient(
                900px 520px at 12% -8%,
                rgba(var(--ion-color-primary-rgb), 0.26),
                transparent 64%
              ),
              radial-gradient(
                860px 500px at 88% 4%,
                rgba(var(--ion-color-secondary-rgb), 0.22),
                transparent 68%
              ),
              radial-gradient(
                760px 460px at 50% 100%,
                rgba(var(--ion-color-tertiary-rgb), 0.16),
                transparent 72%
              ),
              linear-gradient(
                160deg,
                rgba(4, 10, 24, 0.99) 0%,
                rgba(8, 19, 41, 0.97) 48%,
                rgba(14, 30, 62, 0.95) 100%
              ),
              var(--vault-surface);
          }

          .home-hero h1,
          .welcome-text {
            text-shadow: 0 1px 14px rgba(0, 0, 0, 0.28);
          }

          .home-actions ion-button,
          .home-welcome ion-button {
            backdrop-filter: blur(4px);
          }
          
          .home-hero {
            text-align: center;
            margin-bottom: 48px;
          }
          
          .hero-icon {
            font-size: 64px;
            margin-bottom: 16px;
          }
          
          .home-hero h1 {
            font-size: 32px;
            font-weight: 700;
            color: var(--ion-text-color);
            margin: 0 0 8px 0;
          }
          
          .home-hero p {
            font-size: 16px;
            color: var(--ion-color-medium);
            margin: 0;
          }
          
          .home-actions {
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 48px;
          }
          
          .home-welcome {
            text-align: center;
            margin-bottom: 48px;
          }
          
          .welcome-text {
            font-size: 18px;
            margin-bottom: 24px;
            color: var(--ion-text-color);
          }
          
          .home-features {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-top: auto;
          }
          
          .feature-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px;
            background: var(--vault-surface-low);
            border-radius: 12px;
            font-size: 16px;
            color: var(--ion-text-color);
          }
          
          .feature-icon {
            font-size: 24px;
          }

          .home-footer {
            margin-top: 48px;
            text-align: center;
          }
          
          .language-switcher {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-bottom: 16px;
            width: 100%;
          }
          
          .language-switcher ion-item {
            --padding-start: 0;
            --padding-end: 0;
            --inner-padding-start: 0;
            --inner-padding-end: 0;
            --min-height: auto;
            width: auto;
            text-align: center;
          }

          .language-switcher ion-label {
            margin-inline-end: 10px;
            text-align: center;
          }

          .language-switcher ion-select {
            text-align: center;
          }

          .footer-links {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
          }

          .footer-links span {
            color: var(--ion-color-primary);
            font-size: 14px;
            cursor: pointer;
          }

          .footer-links .divider {
            color: var(--ion-color-medium);
            cursor: default;
          }

          .copyright {
            font-size: 12px;
            color: var(--ion-color-medium);
            margin: 0;
          }

          .build-meta {
            font-size: 11px;
            color: var(--ion-color-medium);
            opacity: 0.85;
            margin: 6px 0 0;
            font-variant-numeric: tabular-nums;
            letter-spacing: 0.02em;
          }
        `}</style>
      </IonContent>
    </IonPage>
  );
};

export default HomePage;

